import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import supportApiService from '../../services/supportApiService';

const SupportTickets = () => {
  const navigate = useNavigate();

  // Active tab: 'assigned' = my tickets | 'unassigned' = all open tickets
  const [activeTab, setActiveTab] = useState('assigned');

  // My assigned tickets state
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [assignedTickets, setAssignedTickets] = useState([]);
  const [filteredAssigned, setFilteredAssigned] = useState([]);
  const [assignedSearch, setAssignedSearch] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState(['ASSIGNED', 'RESOLVE', 'INPROGRESS']);

  // Unassigned (OPEN) tickets state
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [unassignedTickets, setUnassignedTickets] = useState([]);
  const [filteredUnassigned, setFilteredUnassigned] = useState([]);
  const [unassignedSearch, setUnassignedSearch] = useState('');
  const [assigningTicketId, setAssigningTicketId] = useState(null);

  const statusOptions = [
    { value: 'ASSIGNED', label: 'Assigned', color: 'bg-blue-100 text-blue-800' },
    { value: 'RESOLVE', label: 'Resolved', color: 'bg-green-100 text-green-800' },
    { value: 'INPROGRESS', label: 'In Progress', color: 'bg-yellow-100 text-yellow-800' },
  ];

  // ─── Load on tab change ───────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab === 'assigned') {
      loadAssignedTickets();
    } else {
      loadUnassignedTickets();
    }
  }, [activeTab, selectedStatuses]);

  // ─── Filter assigned tickets ──────────────────────────────────────────────
  useEffect(() => {
    if (!assignedSearch.trim()) {
      setFilteredAssigned(assignedTickets);
    } else {
      const s = assignedSearch.toLowerCase();
      setFilteredAssigned(assignedTickets.filter(t =>
        t.ticketId?.toLowerCase().includes(s) ||
        t.agentId?.toLowerCase().includes(s) ||
        t.agentName?.toLowerCase().includes(s) ||
        t.agentEmail?.toLowerCase().includes(s) ||
        t.agentQuery?.toLowerCase().includes(s)
      ));
    }
  }, [assignedTickets, assignedSearch]);

  // ─── Filter unassigned tickets ────────────────────────────────────────────
  useEffect(() => {
    if (!unassignedSearch.trim()) {
      setFilteredUnassigned(unassignedTickets);
    } else {
      const s = unassignedSearch.toLowerCase();
      setFilteredUnassigned(unassignedTickets.filter(t =>
        t.ticketId?.toLowerCase().includes(s) ||
        t.agentId?.toLowerCase().includes(s) ||
        t.agentName?.toLowerCase().includes(s) ||
        t.agentEmail?.toLowerCase().includes(s) ||
        t.agentQuery?.toLowerCase().includes(s)
      ));
    }
  }, [unassignedTickets, unassignedSearch]);

  // ─── API: load MY assigned tickets ───────────────────────────────────────
  const loadAssignedTickets = async () => {
    try {
      setAssignedLoading(true);
      const response = await supportApiService.getSupportTickets(selectedStatuses);
      if (response.success) {
        setAssignedTickets(response.tickets || []);
      } else {
        toast.error(response.message || 'Failed to load tickets');
      }
    } catch (err) {
      toast.error('Failed to load tickets');
    } finally {
      setAssignedLoading(false);
    }
  };

  // ─── API: load UNASSIGNED (OPEN) tickets ──────────────────────────────────
  const loadUnassignedTickets = async () => {
    try {
      setUnassignedLoading(true);
      // /all-tickets returns { success, tickets: [OPEN tickets], support: [...] }
      const response = await supportApiService.getAllSupportTickets();
      if (response.success) {
        setUnassignedTickets(response.tickets || []);
      } else {
        toast.error(response.message || 'Failed to load unassigned tickets');
      }
    } catch (err) {
      toast.error('Failed to load unassigned tickets');
    } finally {
      setUnassignedLoading(false);
    }
  };

  // ─── Assign ticket to ME (support) ───────────────────────────────────────
  // Backend: POST /api/support/dashboard/assign-ticket/{ticketId}
  // ticketId here is the ticket's ticketId field (NOT the MongoDB _id)
  const handleAssignToMe = async (e, ticket) => {
    e.stopPropagation();
    const targetTicketId = ticket.ticketId || ticket.id;
    try {
      setAssigningTicketId(targetTicketId);
      const response = await supportApiService.assignTicket(targetTicketId);
      if (response.success) {
        toast.success('Ticket assigned to you successfully!');
        await loadUnassignedTickets();
        await loadAssignedTickets();
      } else {
        toast.error(response.message || 'Failed to assign ticket');
      }
    } catch (err) {
      toast.error('Failed to assign ticket');
    } finally {
      setAssigningTicketId(null);
    }
  };

  const handleStatusToggle = (status) => {
    setSelectedStatuses(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const handleTicketClick = (ticket) => {
    navigate(`/support/dashboard/support-tickets/${ticket.id}`);
  };

  const getStatusBadge = (status) => {
    const map = {
      OPEN: 'bg-orange-100 text-orange-800',
      ASSIGNED: 'bg-blue-100 text-blue-800',
      RESOLVE: 'bg-green-100 text-green-800',
      INPROGRESS: 'bg-yellow-100 text-yellow-800',
      BLOCK: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-800'}`}>
        {status || 'N/A'}
      </span>
    );
  };

  const formatDate = (d) => {
    if (!d) return 'N/A';
    try {
      return new Date(d).toLocaleDateString('en-IN', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch { return d; }
  };

  // ─── Table column header ──────────────────────────────────────────────────
  const TableHead = ({ withAction }) => (
    <thead className="bg-gray-50">
      <tr>
        {['Ticket ID', 'Agent ID', 'Agent Name', 'Agent Email', 'Query', 'Date', 'Status'].map(h => (
          <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
        ))}
        {withAction && (
          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
        )}
      </tr>
    </thead>
  );

  // ─── Table row ────────────────────────────────────────────────────────────
  const TicketRow = ({ ticket, withAction }) => (
    <tr
      className="hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={() => handleTicketClick(ticket)}
    >
      <td className="px-5 py-4 whitespace-nowrap text-sm font-semibold text-green-600">{ticket.ticketId || '—'}</td>
      <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-700">{ticket.agentId || '—'}</td>
      <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-700">{ticket.agentName || '—'}</td>
      <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-700">{ticket.agentEmail || '—'}</td>
      <td className="px-5 py-4 text-sm text-gray-700 max-w-xs">
        <div className="truncate" title={ticket.agentQuery}>{ticket.truncatedQuery || ticket.agentQuery || '—'}</div>
      </td>
      <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-700">{formatDate(ticket.createdDate)}</td>
      <td className="px-5 py-4 whitespace-nowrap">{getStatusBadge(ticket.ticketStatus)}</td>
      {withAction && (
        <td className="px-5 py-4 whitespace-nowrap" onClick={e => e.stopPropagation()}>
          <button
            onClick={(e) => handleAssignToMe(e, ticket)}
            disabled={assigningTicketId === ticket.ticketId}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {assigningTicketId === ticket.ticketId ? (
              <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div> Assigning...</>
            ) : (
              <><i className="fas fa-user-plus"></i> Assign to Me</>
            )}
          </button>
        </td>
      )}
    </tr>
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-7xl">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Support Tickets</h1>
          <p className="text-gray-600 mt-1">Manage and track support tickets from agents</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-orange-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg"><i className="fas fa-inbox text-orange-500"></i></div>
              <div>
                <p className="text-xs text-gray-500">Unassigned</p>
                <p className="text-xl font-bold text-gray-900">{unassignedTickets.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><i className="fas fa-user-check text-blue-500"></i></div>
              <div>
                <p className="text-xs text-gray-500">My Assigned</p>
                <p className="text-xl font-bold text-gray-900">{assignedTickets.filter(t => t.ticketStatus === 'ASSIGNED').length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-yellow-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg"><i className="fas fa-clock text-yellow-500"></i></div>
              <div>
                <p className="text-xs text-gray-500">In Progress</p>
                <p className="text-xl font-bold text-gray-900">{assignedTickets.filter(t => t.ticketStatus === 'INPROGRESS').length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-green-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg"><i className="fas fa-check-circle text-green-500"></i></div>
              <div>
                <p className="text-xs text-gray-500">Resolved</p>
                <p className="text-xl font-bold text-gray-900">{assignedTickets.filter(t => t.ticketStatus === 'RESOLVE').length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex gap-6">
            <button
              onClick={() => setActiveTab('assigned')}
              className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'assigned'
                  ? 'border-green-600 text-green-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="fas fa-ticket-alt mr-2"></i>
              My Assigned Tickets
              <span className="ml-2 bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                {assignedTickets.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('unassigned')}
              className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'unassigned'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="fas fa-inbox mr-2"></i>
              Unassigned Tickets
              {unassignedTickets.length > 0 && (
                <span className="ml-2 bg-orange-100 text-orange-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {unassignedTickets.length}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* ─── TAB: My Assigned Tickets ───────────────────────────────────── */}
        {activeTab === 'assigned' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Filters */}
            <div className="px-6 py-4 border-b border-gray-200 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex flex-wrap gap-4 items-center">
                <span className="text-sm font-medium text-gray-700">Filter by Status:</span>
                {statusOptions.map(s => (
                  <label key={s.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(s.value)}
                      onChange={() => handleStatusToggle(s.value)}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700">{s.label}</span>
                  </label>
                ))}
              </div>
              <div className="relative max-w-xs w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-gray-400 text-sm"></i>
                </div>
                <input
                  type="text"
                  value={assignedSearch}
                  onChange={e => setAssignedSearch(e.target.value)}
                  placeholder="Search tickets..."
                  className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                />
              </div>
            </div>

            {/* Table */}
            {assignedLoading ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
              </div>
            ) : filteredAssigned.length === 0 ? (
              <div className="p-10 text-center text-gray-500">
                <i className="fas fa-ticket-alt text-3xl text-gray-300 mb-3 block"></i>
                {assignedSearch ? 'No tickets match your search.' : 'No tickets assigned to you yet.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <TableHead withAction={false} />
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAssigned.map((ticket, i) => (
                      <TicketRow key={ticket.id || i} ticket={ticket} withAction={false} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="px-6 py-3 bg-gray-50 text-sm text-gray-500 border-t border-gray-200">
              Showing {filteredAssigned.length} of {assignedTickets.length} tickets
            </div>
          </div>
        )}

        {/* ─── TAB: Unassigned Tickets ────────────────────────────────────── */}
        {activeTab === 'unassigned' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Info banner */}
            <div className="px-6 py-3 bg-orange-50 border-b border-orange-100 flex items-center gap-2 text-sm text-orange-700">
              <i className="fas fa-info-circle"></i>
              These are unassigned (OPEN) tickets. Click <strong>Assign to Me</strong> to take ownership.
            </div>

            {/* Search */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="relative max-w-xs">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-gray-400 text-sm"></i>
                </div>
                <input
                  type="text"
                  value={unassignedSearch}
                  onChange={e => setUnassignedSearch(e.target.value)}
                  placeholder="Search unassigned tickets..."
                  className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                />
              </div>
            </div>

            {/* Table */}
            {unassignedLoading ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
              </div>
            ) : filteredUnassigned.length === 0 ? (
              <div className="p-10 text-center text-gray-500">
                <i className="fas fa-inbox text-3xl text-gray-300 mb-3 block"></i>
                {unassignedSearch ? 'No tickets match your search.' : 'No unassigned tickets at this time.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <TableHead withAction={true} />
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUnassigned.map((ticket, i) => (
                      <TicketRow key={ticket.id || i} ticket={ticket} withAction={true} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="px-6 py-3 bg-gray-50 text-sm text-gray-500 border-t border-gray-200">
              Showing {filteredUnassigned.length} of {unassignedTickets.length} unassigned tickets
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default SupportTickets;